// RAG Admin Page - Document pipeline management with tabbed interface

import { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button, StatusBadge, Tabs, TabList, Tab, TabPanel } from '../components/common';
import { DocumentViewerModal, HtmlPageViewerModal } from '../components/rag';
import { IngestionAdminTab } from '../components/rag/IngestionAdminTab';
import { formatDate, formatFileSize } from '../utils/dateFormatters';
import { adminService, type ChunkLLMPayload, type SearchResultLLMPayload, type SearchResultItem } from '../services/admin';
import type {
  RagDocument,
  RagStats,
  RagDocumentDetail,
  RagChunk,
  SearchConfig,
  SearchResult,
  SearchConfigSnapshot,
  RAGTestCase,
  TestSuiteRun,
  RerankerModel,
  QueryExpansion,
  SpellCorrection,
  DocumentSyncJob,
  DocumentSyncHealth,
  HtmlPage,
  HtmlPageDetail,
  HtmlPagesStats,
  TripReportChunk,
  TripReportChunksStats,
} from '../types/api';
import { RefreshIcon, PlayIcon, SearchIcon, RetryIcon, TrashIcon } from '../components/icons';
import styles from './RagAdminPage.module.css';

// Document type badge
function TypeBadge({ type }: { type: string }) {
  return (
    <span className={`${styles.typeBadge} ${styles[type] || styles.unknown}`}>
      {type}
    </span>
  );
}

// Processing status badge
function ProcessingStatusBadge({ status }: { status: string }) {
  const variant = {
    completed: 'success',
    uploaded: 'success',
    chunked: 'info',
    extracted: 'info',
    downloaded: 'info',
    pending: 'warning',
    running: 'info',
    failed: 'error',
    removed: 'error',
  }[status] || 'warning';

  return (
    <StatusBadge variant={variant as 'success' | 'error' | 'warning' | 'info'} pulse={status === 'running'}>
      {status}
    </StatusBadge>
  );
}

// Processing indicator for header
function ProcessingIndicator({ count }: { count: number }) {
  if (count === 0) return null;

  return (
    <span className={styles.processingIndicator}>
      <span className={styles.processingDot} />
      {count} processing
    </span>
  );
}

export function RagAdminPage() {
  const [stats, setStats] = useState<RagStats | null>(null);
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<string>('documents');

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const [offset, setOffset] = useState(0);
  const limit = 25;

  // Selected document for detail view
  const [selectedDoc, setSelectedDoc] = useState<RagDocumentDetail | null>(null);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);

  // Process document state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<{
    status: string;
    filename?: string;
    chunk_count?: number;
    error?: string;
    remaining_candidates?: number;
    message?: string;
  } | null>(null);

  // Search state with full config
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchConfig, setSearchConfig] = useState<Omit<SearchConfig, 'query'>>({
    page_size: 10,
    top_k: 20,
    filters: {
      doc_types: [],
      document_types: [],
      categories: [],
      tags: [],
      source_documents: [],
      page_types: [],
    },
    query_expansion: 'AUTO',
    spell_correction: 'AUTO',
    enable_reranking: true,
    reranker_model: 'semantic-ranker-default-004',
    rerank_top_n: 10,
    // Note: include_snippets and max_extractive_answers require Enterprise Edition
    // Set to false/0 for Standard Edition engines
    include_snippets: false,
    max_extractive_answers: 0,
  });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [expandedSearchResultIds, setExpandedSearchResultIds] = useState<Set<string>>(new Set());

  // A/B Testing state
  const [savedConfigs, setSavedConfigs] = useState<SearchConfigSnapshot[]>([]);
  const [testCases, setTestCases] = useState<RAGTestCase[]>([]);
  const [testRuns, setTestRuns] = useState<TestSuiteRun[]>([]);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  const [isLoadingTestCases, setIsLoadingTestCases] = useState(false);
  const [isRunningTestSuite, setIsRunningTestSuite] = useState(false);
  const [selectedConfigForTest, setSelectedConfigForTest] = useState<string>('');
  const [saveConfigName, setSaveConfigName] = useState('');
  const [saveConfigDescription, setSaveConfigDescription] = useState('');

  // Action state for retry/remove operations
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Chunks tab state
  const [chunks, setChunks] = useState<RagChunk[]>([]);
  const [chunksTotal, setChunksTotal] = useState(0);
  const [chunksOffset, setChunksOffset] = useState(0);
  const chunksLimit = 25;
  const [isLoadingChunks, setIsLoadingChunks] = useState(false);
  const [chunksUselessFilter, setChunksUselessFilter] = useState<string>('');
  const [chunksSummaryFilter, setChunksSummaryFilter] = useState<string>('');
  const [chunksSearch, setChunksSearch] = useState('');
  const [expandedChunkIds, setExpandedChunkIds] = useState<Set<string>>(new Set());

  // LLM Payload modal state - supports both chunk and search result payloads
  const [llmPayload, setLlmPayload] = useState<ChunkLLMPayload | SearchResultLLMPayload | null>(null);
  const [isLoadingLlmPayload, setIsLoadingLlmPayload] = useState(false);

  // Sync Jobs tab state
  const [syncJobs, setSyncJobs] = useState<DocumentSyncJob[]>([]);
  const [syncJobsTotal, setSyncJobsTotal] = useState(0);
  const [syncJobsOffset, setSyncJobsOffset] = useState(0);
  const syncJobsLimit = 15;
  const [isLoadingSyncJobs, setIsLoadingSyncJobs] = useState(false);
  const [syncHealth, setSyncHealth] = useState<DocumentSyncHealth | null>(null);
  const [isTriggeringSync, setIsTriggeringSync] = useState(false);
  const [syncBatchLimit, setSyncBatchLimit] = useState(10);

  // HTML Pages tab state
  const [htmlPages, setHtmlPages] = useState<HtmlPage[]>([]);
  const [htmlPagesTotal, setHtmlPagesTotal] = useState(0);
  const [htmlPagesOffset, setHtmlPagesOffset] = useState(0);
  const htmlPagesLimit = 25;
  const [isLoadingHtmlPages, setIsLoadingHtmlPages] = useState(false);
  const [htmlPagesStats, setHtmlPagesStats] = useState<HtmlPagesStats | null>(null);
  const [htmlPageStatusFilter, setHtmlPageStatusFilter] = useState<string>('');
  const [htmlPageTypeFilter, setHtmlPageTypeFilter] = useState<string>('');
  const [htmlPageSearch, setHtmlPageSearch] = useState<string>('');
  const [selectedHtmlPage, setSelectedHtmlPage] = useState<HtmlPageDetail | null>(null);
  const [isLoadingHtmlPage, setIsLoadingHtmlPage] = useState(false);
  const [syncTriggerResult, setSyncTriggerResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Trip Report Chunks tab state
  const [tripChunks, setTripChunks] = useState<TripReportChunk[]>([]);
  const [tripChunksTotal, setTripChunksTotal] = useState(0);
  const [tripChunksOffset, setTripChunksOffset] = useState(0);
  const tripChunksLimit = 25;
  const [isLoadingTripChunks, setIsLoadingTripChunks] = useState(false);
  const [tripChunksStats, setTripChunksStats] = useState<TripReportChunksStats | null>(null);
  const [tripChunksTypeFilter, setTripChunksTypeFilter] = useState<string>('');
  const [tripChunksRawTextFilter, setTripChunksRawTextFilter] = useState<string>('');
  const [tripChunksSearch, setTripChunksSearch] = useState('');
  const [expandedTripChunkIds, setExpandedTripChunkIds] = useState<Set<string>>(new Set());

  // Calculate running/pending count for badge
  const processingCount = stats?.documents.by_status.running ?? 0;

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [statsData, docsData] = await Promise.all([
        adminService.getRagStats(),
        adminService.getRagDocuments({
          limit,
          offset,
          status: statusFilter || undefined,
          document_type: typeFilter || undefined,
          include_inactive: includeInactive,
        }),
      ]);
      setStats(statsData);
      setDocuments(docsData.documents);
      setTotal(docsData.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch RAG data');
    } finally {
      setIsLoading(false);
    }
  }, [offset, statusFilter, typeFilter, includeInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [statusFilter, typeFilter, includeInactive]);

  // Fetch chunks for Chunks tab
  const fetchChunks = useCallback(async () => {
    setIsLoadingChunks(true);
    try {
      const params: {
        limit: number;
        offset: number;
        is_useless?: boolean;
        has_summary?: boolean;
        search?: string;
      } = {
        limit: chunksLimit,
        offset: chunksOffset,
      };

      if (chunksUselessFilter === 'true') params.is_useless = true;
      else if (chunksUselessFilter === 'false') params.is_useless = false;

      if (chunksSummaryFilter === 'true') params.has_summary = true;
      else if (chunksSummaryFilter === 'false') params.has_summary = false;

      if (chunksSearch.trim()) params.search = chunksSearch.trim();

      const response = await adminService.getRagChunks(params);
      setChunks(response.chunks);
      setChunksTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch chunks');
    } finally {
      setIsLoadingChunks(false);
    }
  }, [chunksOffset, chunksUselessFilter, chunksSummaryFilter, chunksSearch]);

  // Reset chunks offset when filters change
  useEffect(() => {
    setChunksOffset(0);
  }, [chunksUselessFilter, chunksSummaryFilter, chunksSearch]);

  const toggleChunkExpand = (chunkId: string) => {
    setExpandedChunkIds(prev => {
      const next = new Set(prev);
      if (next.has(chunkId)) {
        next.delete(chunkId);
      } else {
        next.add(chunkId);
      }
      return next;
    });
  };

  const toggleSearchResultExpand = (resultId: string) => {
    setExpandedSearchResultIds(prev => {
      const next = new Set(prev);
      if (next.has(resultId)) {
        next.delete(resultId);
      } else {
        next.add(resultId);
      }
      return next;
    });
  };

  // Load LLM payload for a chunk
  const loadLlmPayload = async (chunkId: string) => {
    setIsLoadingLlmPayload(true);
    try {
      const payload = await adminService.getChunkLLMPayload(chunkId);
      setLlmPayload(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load LLM payload');
    } finally {
      setIsLoadingLlmPayload(false);
    }
  };

  const handleDocumentClick = async (docId: string) => {
    setIsLoadingDoc(true);
    try {
      const doc = await adminService.getRagDocument(docId);
      setSelectedDoc(doc);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch document');
    } finally {
      setIsLoadingDoc(false);
    }
  };

  const handleProcessNext = async (sync: boolean = false) => {
    setIsProcessing(true);
    setProcessResult(null);
    try {
      const result = await adminService.processNextRagDocument(sync);

      if (result.status === 'queued' && result.task_id) {
        // Start polling for task completion
        setProcessResult({
          status: 'processing',
          filename: result.filename,
          message: `Processing ${result.filename}...`,
        });

        // Poll for task status
        const pollInterval = setInterval(async () => {
          try {
            const taskStatus = await adminService.getTaskStatus(result.task_id!);

            if (taskStatus.state === 'SUCCESS') {
              clearInterval(pollInterval);
              setProcessResult(taskStatus.result || { status: 'completed' });
              setIsProcessing(false);
              await fetchData();
            } else if (taskStatus.state === 'FAILURE') {
              clearInterval(pollInterval);
              setProcessResult({
                status: 'failed',
                error: taskStatus.error || 'Task failed',
              });
              setIsProcessing(false);
            }
            // Keep polling for PENDING, STARTED, RETRY states
          } catch (pollErr) {
            clearInterval(pollInterval);
            setProcessResult({
              status: 'failed',
              error: 'Failed to check task status',
            });
            setIsProcessing(false);
          }
        }, 3000); // Poll every 3 seconds

        // Stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (isProcessing) {
            setProcessResult({
              status: 'timeout',
              message: 'Processing is taking longer than expected. Check the Documents tab for status.',
            });
            setIsProcessing(false);
          }
        }, 300000);
      } else {
        // Synchronous result or no candidates
        setProcessResult(result);
        if (result.status === 'completed') {
          await fetchData();
        }
        setIsProcessing(false);
      }
    } catch (err) {
      setProcessResult({
        status: 'failed',
        error: err instanceof Error ? err.message : 'Failed to process document',
      });
      setIsProcessing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setSearchResults(null);
    try {
      const fullConfig: SearchConfig = {
        ...searchConfig,
        query: searchQuery.trim(),
      };
      const results = await adminService.searchAdvanced(fullConfig);
      setSearchResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const fetchTestingData = useCallback(async () => {
    setIsLoadingConfigs(true);
    setIsLoadingTestCases(true);
    try {
      const [configs, cases, runs] = await Promise.all([
        adminService.listSearchConfigs(),
        adminService.listTestCases(),
        adminService.listTestRuns({ limit: 10 }),
      ]);
      setSavedConfigs(configs);
      setTestCases(cases);
      setTestRuns(runs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load testing data');
    } finally {
      setIsLoadingConfigs(false);
      setIsLoadingTestCases(false);
    }
  }, []);

  const handleSaveConfig = async () => {
    if (!saveConfigName.trim()) return;
    try {
      const fullConfig: SearchConfig = {
        ...searchConfig,
        query: searchQuery || 'placeholder',
        config_name: saveConfigName,
      };
      await adminService.saveSearchConfig(fullConfig, saveConfigName, saveConfigDescription);
      setSaveConfigName('');
      setSaveConfigDescription('');
      await fetchTestingData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    }
  };

  const handleRunTestSuite = async () => {
    if (!selectedConfigForTest) return;
    setIsRunningTestSuite(true);
    try {
      await adminService.runTestSuite(selectedConfigForTest);
      await fetchTestingData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run test suite');
    } finally {
      setIsRunningTestSuite(false);
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (!confirm('Deactivate this configuration?')) return;
    try {
      await adminService.deactivateSearchConfig(configId);
      await fetchTestingData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate config');
    }
  };

  // Fetch sync jobs data
  const fetchSyncJobs = useCallback(async () => {
    setIsLoadingSyncJobs(true);
    try {
      const [jobsData, healthData] = await Promise.all([
        adminService.getDocumentSyncJobs({
          limit: syncJobsLimit,
          offset: syncJobsOffset,
        }),
        adminService.getDocumentSyncHealth(),
      ]);
      setSyncJobs(jobsData.jobs);
      setSyncJobsTotal(jobsData.total);
      setSyncHealth(healthData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sync jobs');
    } finally {
      setIsLoadingSyncJobs(false);
    }
  }, [syncJobsOffset]);

  // Handle triggering a sync
  const handleTriggerSync = async () => {
    if (!confirm(`Start document sync with batch limit of ${syncBatchLimit}?`)) return;
    setIsTriggeringSync(true);
    setSyncTriggerResult(null);
    try {
      const result = await adminService.triggerDocumentSync(syncBatchLimit);
      setSyncTriggerResult({
        type: 'success',
        message: `Sync triggered! Task ID: ${result.task_id}`,
      });
      // Refresh jobs list after a short delay
      setTimeout(() => {
        fetchSyncJobs();
      }, 2000);
    } catch (err) {
      setSyncTriggerResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to trigger sync',
      });
    } finally {
      setIsTriggeringSync(false);
    }
  };

  // Calculate duration string from timestamps
  const formatDuration = (startedAt: string, completedAt: string | null): string => {
    if (!completedAt) return 'In progress';
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Fetch HTML pages
  const fetchHtmlPages = useCallback(async () => {
    setIsLoadingHtmlPages(true);
    try {
      const [pagesData, statsData] = await Promise.all([
        adminService.getHtmlPages({
          limit: htmlPagesLimit,
          offset: htmlPagesOffset,
          status: htmlPageStatusFilter || undefined,
          page_type: htmlPageTypeFilter || undefined,
          search: htmlPageSearch || undefined,
        }),
        adminService.getHtmlPagesStats(),
      ]);
      setHtmlPages(pagesData.pages);
      setHtmlPagesTotal(pagesData.total);
      setHtmlPagesStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch HTML pages');
    } finally {
      setIsLoadingHtmlPages(false);
    }
  }, [htmlPagesOffset, htmlPageStatusFilter, htmlPageTypeFilter, htmlPageSearch]);

  // Refetch HTML pages when filters change
  useEffect(() => {
    // Only refetch if the tab has been activated (pages have been loaded at least once)
    if (htmlPages.length > 0 || htmlPagesTotal > 0) {
      fetchHtmlPages();
    }
  }, [htmlPageStatusFilter, htmlPageTypeFilter, htmlPageSearch, fetchHtmlPages]);

  // Fetch HTML page detail for viewer
  const handleViewHtmlPage = async (pageId: string) => {
    setIsLoadingHtmlPage(true);
    try {
      const detail = await adminService.getHtmlPage(pageId);
      setSelectedHtmlPage(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch HTML page');
    } finally {
      setIsLoadingHtmlPage(false);
    }
  };

  // Fetch trip report chunks
  const fetchTripChunks = useCallback(async () => {
    setIsLoadingTripChunks(true);
    try {
      const params: {
        limit: number;
        offset: number;
        chunk_type?: string;
        has_raw_text?: boolean;
        search?: string;
      } = {
        limit: tripChunksLimit,
        offset: tripChunksOffset,
      };

      if (tripChunksTypeFilter) params.chunk_type = tripChunksTypeFilter;

      if (tripChunksRawTextFilter === 'true') params.has_raw_text = true;
      else if (tripChunksRawTextFilter === 'false') params.has_raw_text = false;

      if (tripChunksSearch.trim()) params.search = tripChunksSearch.trim();

      const [chunksData, statsData] = await Promise.all([
        adminService.getTripReportChunks(params),
        adminService.getTripReportChunksStats(),
      ]);
      setTripChunks(chunksData.chunks);
      setTripChunksTotal(chunksData.total);
      setTripChunksStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch trip report chunks');
    } finally {
      setIsLoadingTripChunks(false);
    }
  }, [tripChunksOffset, tripChunksTypeFilter, tripChunksRawTextFilter, tripChunksSearch]);

  // Reset trip chunks offset when filters change
  useEffect(() => {
    setTripChunksOffset(0);
  }, [tripChunksTypeFilter, tripChunksRawTextFilter, tripChunksSearch]);

  const toggleTripChunkExpand = (chunkId: string) => {
    setExpandedTripChunkIds(prev => {
      const next = new Set(prev);
      if (next.has(chunkId)) {
        next.delete(chunkId);
      } else {
        next.add(chunkId);
      }
      return next;
    });
  };

  const handleRetryDocument = async (e: React.MouseEvent, docId: string, filename: string) => {
    e.stopPropagation(); // Prevent row click
    if (!confirm(`Retry processing for "${filename}"?`)) return;

    setActionInProgress(docId);
    setActionMessage(null);
    try {
      await adminService.retryDocument(docId);
      setActionMessage({ type: 'success', text: `Retry triggered for ${filename}` });
      await fetchData();
    } catch (err) {
      setActionMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to retry document'
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRemoveDocument = async (e: React.MouseEvent, docId: string, filename: string) => {
    e.stopPropagation(); // Prevent row click
    if (!confirm(`Remove "${filename}" and all its data? This cannot be undone.`)) return;

    setActionInProgress(docId);
    setActionMessage(null);
    try {
      const result = await adminService.removeDocument(docId);
      setActionMessage({
        type: 'success',
        text: `Removed ${filename} (${result.removed_chunks} chunks)`
      });
      await fetchData();
    } catch (err) {
      setActionMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to remove document'
      });
    } finally {
      setActionInProgress(null);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading RAG pipeline data...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h1>RAG Pipeline Admin</h1>
          <ProcessingIndicator count={processingCount} />
        </div>
      </div>

      {error && (
        <Card className={styles.errorCard}>
          <CardBody>
            <p className={styles.errorText}>{error}</p>
          </CardBody>
        </Card>
      )}

      <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
        <TabList>
          <Tab value="documents">Documents</Tab>
          <Tab value="chunks">Chunks</Tab>
          <Tab value="htmlpages">HTML Pages</Tab>
          <Tab value="syncjobs">Sync Jobs</Tab>
          <Tab value="tripreports">Trip Reports</Tab>
          <Tab value="search">Search</Tab>
          <Tab value="testing">Testing</Tab>
          <Tab value="ingestion">Ingestion</Tab>
        </TabList>

        {/* Documents Tab */}
        <TabPanel value="documents">
          <div className={styles.tabHeader}>
            <div className={styles.filters}>
              <select
                className={styles.filterSelect}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="downloaded">Downloaded</option>
                <option value="extracted">Extracted</option>
                <option value="chunked">Chunked</option>
                <option value="uploaded">Uploaded</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="removed">Removed</option>
              </select>
              <select
                className={styles.filterSelect}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="sds">SDS</option>
                <option value="tds">TDS</option>
                <option value="leed">LEED</option>
                <option value="coa">COA</option>
                <option value="spec">SPEC</option>
                <option value="unknown">Unknown</option>
              </select>
              <label className={styles.checkboxLabel} style={{ marginLeft: '8px' }}>
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                Include inactive/removed
              </label>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              leftIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </div>

          {/* Action Message */}
          {actionMessage && (
            <div className={actionMessage.type === 'success' ? styles.actionSuccess : styles.actionError}>
              {actionMessage.text}
              <button
                className={styles.dismissButton}
                onClick={() => setActionMessage(null)}
              >
                &times;
              </button>
            </div>
          )}

          <Card padding="none" className={styles.tableCard}>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Size</th>
                    <th>Chunks</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.length > 0 ? (
                    documents.map((doc) => (
                      <tr
                        key={doc.id}
                        onClick={() => handleDocumentClick(doc.id)}
                        className={!doc.is_active ? styles.inactiveRow : undefined}
                      >
                        <td>
                          <div className={styles.filename}>
                            {doc.filename}
                            {!doc.is_active && (
                              <span className={styles.inactiveBadge}>INACTIVE</span>
                            )}
                          </div>
                          <div className={styles.ftpPath}>{doc.ftp_path}</div>
                          {doc.removed_at && (
                            <div className={styles.removedAt}>
                              Removed: {formatDate(doc.removed_at)}
                              {doc.deletion_reason && (
                                <span className={styles.deletionReason}>
                                  ({doc.deletion_reason === 'ftp_sync' ? 'FTP sync' : doc.deletion_reason === 'manual' ? 'Manual' : 'Superseded'})
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td><TypeBadge type={doc.document_type} /></td>
                        <td><ProcessingStatusBadge status={doc.processing_status} /></td>
                        <td>{formatFileSize(doc.file_size)}</td>
                        <td>
                          <div className={styles.chunkStats}>
                            <span>{doc.chunk_count}</span>
                            {doc.chunk_count > 0 && (
                              <span className={styles.chunkDetail}>
                                {doc.summarized_chunks} summarized, {doc.useless_chunks} useless
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{formatDate(doc.updated_at)}</td>
                        <td>
                          <div className={styles.actionButtons}>
                            {doc.processing_status === 'failed' && (
                              <button
                                className={styles.actionButton}
                                onClick={(e) => handleRetryDocument(e, doc.id, doc.filename)}
                                disabled={actionInProgress === doc.id}
                                title="Retry processing"
                              >
                                {actionInProgress === doc.id ? '...' : <RetryIcon />}
                              </button>
                            )}
                            {doc.is_active && (
                              <button
                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                onClick={(e) => handleRemoveDocument(e, doc.id, doc.filename)}
                                disabled={actionInProgress === doc.id}
                                title="Remove document"
                              >
                                {actionInProgress === doc.id ? '...' : <TrashIcon />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className={styles.emptyRow}>
                        No documents found matching filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={styles.pagination}>
              <span className={styles.paginationInfo}>
                Showing {offset + 1}-{Math.min(offset + limit, total)} of {total} documents
              </span>
              <div className={styles.paginationButtons}>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}
                >
                  Next
                </Button>
              </div>
            </div>
          </Card>
        </TabPanel>

        {/* Chunks Tab */}
        <TabPanel value="chunks" onActivate={fetchChunks}>
          {/* Stats Summary */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Total</span>
              <span className={styles.statValue}>{stats?.chunks.total?.toLocaleString() ?? 0}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Useful</span>
              <span className={styles.statValue}>{stats?.chunks.useful?.toLocaleString() ?? 0}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Useless</span>
              <span className={styles.statValue}>{stats?.chunks.useless?.toLocaleString() ?? 0}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>With Summary</span>
              <span className={styles.statValue}>{stats?.chunks.with_summary?.toLocaleString() ?? 0}</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Total Tokens</span>
              <span className={styles.statValue}>{stats?.chunks.total_tokens?.toLocaleString() ?? 0}</span>
            </div>
          </div>

          {/* Filters */}
          <div className={styles.tabHeader}>
            <div className={styles.filters}>
              <select
                className={styles.filterSelect}
                value={chunksUselessFilter}
                onChange={(e) => setChunksUselessFilter(e.target.value)}
              >
                <option value="">All Chunks</option>
                <option value="false">Useful Only</option>
                <option value="true">Useless Only</option>
              </select>
              <select
                className={styles.filterSelect}
                value={chunksSummaryFilter}
                onChange={(e) => setChunksSummaryFilter(e.target.value)}
              >
                <option value="">All Summaries</option>
                <option value="true">With Summary</option>
                <option value="false">No Summary</option>
              </select>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search chunk text..."
                value={chunksSearch}
                onChange={(e) => setChunksSearch(e.target.value)}
                style={{ maxWidth: '250px' }}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchChunks}
              leftIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </div>

          {/* Chunks List */}
          <Card padding="none" className={styles.tableCard}>
            {isLoadingChunks ? (
              <div className={styles.loading}>
                <div className={styles.spinner} />
                <p>Loading chunks...</p>
              </div>
            ) : (
              <>
                <div className={styles.chunksList}>
                  {chunks.length > 0 ? (
                    chunks.map((chunk) => {
                      const isExpanded = expandedChunkIds.has(chunk.id);
                      return (
                        <div
                          key={chunk.id}
                          className={`${styles.chunkListItem} ${isExpanded ? styles.expanded : ''}`}
                        >
                          <div className={styles.chunkListHeader} onClick={() => toggleChunkExpand(chunk.id)}>
                            <div className={styles.chunkListLeft}>
                              <span className={styles.chunkListIndex}>#{chunk.chunk_index}</span>
                              <span className={styles.chunkListFilename}>{chunk.document_filename}</span>
                              <span className={styles.chunkListMeta}>{chunk.content_type}</span>
                              <span className={styles.chunkListMeta}>{chunk.token_count} tok</span>
                              {chunk.start_page && <span className={styles.chunkListMeta}>p{chunk.start_page}</span>}
                            </div>
                            <div className={styles.chunkListRight}>
                              {chunk.is_useless && <StatusBadge variant="warning">Skip</StatusBadge>}
                              {chunk.summary && <StatusBadge variant="success">Summary</StatusBadge>}
                              {chunk.has_bounding_regions && <StatusBadge variant="info">Bounds</StatusBadge>}
                              <span className={styles.expandIndicator}>{isExpanded ? '−' : '+'}</span>
                            </div>
                          </div>

                          {/* Preview line */}
                          {!isExpanded && (
                            <div className={styles.chunkListPreview}>
                              {chunk.summary || chunk.chunk_text}
                            </div>
                          )}

                          {/* Expanded content */}
                          {isExpanded && (
                            <div className={styles.chunkListExpanded}>
                              {chunk.heading_context && (
                                <div className={styles.chunkListHeading}>
                                  <strong>Section:</strong> {chunk.heading_context}
                                </div>
                              )}
                              {/* Show merged sections if this chunk was created from multiple sections */}
                              {chunk.merged_count && chunk.merged_count > 1 && chunk.merged_sections && (
                                <div className={styles.chunkListMerged}>
                                  <strong>Merged from {chunk.merged_count} sections:</strong>
                                  <ul>
                                    {chunk.merged_sections.map((section, idx) => (
                                      <li key={idx}>{section || '(no heading)'}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {chunk.summary && (
                                <div className={styles.chunkListSummary}>
                                  <strong>Summary:</strong> {chunk.summary}
                                </div>
                              )}
                              <div className={styles.chunkListText}>
                                <strong>Raw Text:</strong>
                                <pre>{chunk.chunk_text}</pre>
                              </div>
                              <div className={styles.chunkListFooter}>
                                <span>Tokens: {chunk.token_count}</span>
                                {chunk.summary_tokens > 0 && <span>Summary Tokens: {chunk.summary_tokens}</span>}
                                <span>Bounding Regions: {chunk.has_bounding_regions ? 'Yes' : 'No'}</span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    loadLlmPayload(chunk.id);
                                  }}
                                  disabled={isLoadingLlmPayload}
                                >
                                  View LLM Payload
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.emptySearch}>
                      No chunks found matching filters
                    </div>
                  )}
                </div>

                {/* Pagination */}
                <div className={styles.pagination}>
                  <span className={styles.paginationInfo}>
                    Showing {chunksOffset + 1}-{Math.min(chunksOffset + chunksLimit, chunksTotal)} of {chunksTotal} chunks
                  </span>
                  <div className={styles.paginationButtons}>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={chunksOffset === 0}
                      onClick={() => setChunksOffset(Math.max(0, chunksOffset - chunksLimit))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={chunksOffset + chunksLimit >= chunksTotal}
                      onClick={() => setChunksOffset(chunksOffset + chunksLimit)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </TabPanel>

        {/* HTML Pages Tab */}
        <TabPanel value="htmlpages" onActivate={fetchHtmlPages}>
          {/* HTML Pages Stats Summary */}
          {htmlPagesStats && (
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Total Pages</span>
                <span className={styles.statValue}>{htmlPagesStats.pages.total}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Total Chunks</span>
                <span className={styles.statValue}>{htmlPagesStats.chunks.total}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Useful Chunks</span>
                <span className={styles.statValue}>{htmlPagesStats.chunks.useful}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Indexed Chunks</span>
                <span className={styles.statValue}>{htmlPagesStats.chunks.indexed}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Summarized</span>
                <span className={styles.statValue}>{htmlPagesStats.chunks.with_summary}</span>
              </div>
            </div>
          )}

          <div className={styles.tabHeader}>
            <div className={styles.filters}>
              <select
                className={styles.filterSelect}
                value={htmlPageStatusFilter}
                onChange={(e) => {
                  setHtmlPageStatusFilter(e.target.value);
                  setHtmlPagesOffset(0);
                }}
              >
                <option value="">All Statuses</option>
                <option value="crawled">Crawled</option>
                <option value="extracted">Extracted</option>
                <option value="chunked">Chunked</option>
                <option value="summarized">Summarized</option>
                <option value="indexed">Indexed</option>
                <option value="failed">Failed</option>
              </select>
              <select
                className={styles.filterSelect}
                value={htmlPageTypeFilter}
                onChange={(e) => {
                  setHtmlPageTypeFilter(e.target.value);
                  setHtmlPagesOffset(0);
                }}
              >
                <option value="">All Types</option>
                <option value="product">Product</option>
                <option value="resource">Resource</option>
                <option value="support">Support</option>
                <option value="market">Market</option>
                <option value="about">About</option>
                <option value="general">General</option>
              </select>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search URL or title..."
                value={htmlPageSearch}
                onChange={(e) => {
                  setHtmlPageSearch(e.target.value);
                  setHtmlPagesOffset(0);
                }}
              />
            </div>
            <div className={styles.pagination}>
              <Button
                variant="outline"
                size="sm"
                disabled={htmlPagesOffset === 0}
                onClick={() => setHtmlPagesOffset(Math.max(0, htmlPagesOffset - htmlPagesLimit))}
              >
                Previous
              </Button>
              <span className={styles.pageInfo}>
                {htmlPagesOffset + 1}-{Math.min(htmlPagesOffset + htmlPagesLimit, htmlPagesTotal)} of {htmlPagesTotal}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={htmlPagesOffset + htmlPagesLimit >= htmlPagesTotal}
                onClick={() => setHtmlPagesOffset(htmlPagesOffset + htmlPagesLimit)}
              >
                Next
              </Button>
            </div>
          </div>

          <Card>
            {isLoadingHtmlPages ? (
              <CardBody>
                <div className={styles.loading}>
                  <div className={styles.spinner} />
                  <p>Loading HTML pages...</p>
                </div>
              </CardBody>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Chunks</th>
                      <th>Useful</th>
                      <th>Crawled</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {htmlPages.map((page) => (
                      <tr
                        key={page.id}
                        onClick={() => handleViewHtmlPage(page.id)}
                        className={styles.clickableRow}
                      >
                        <td>
                          <div className={styles.titleCell}>
                            <span className={styles.docTitle} title={page.title}>
                              {page.title.length > 50 ? page.title.substring(0, 50) + '...' : page.title}
                            </span>
                            <span className={styles.docPath} title={page.url}>
                              {page.url.length > 60 ? '...' + page.url.substring(page.url.length - 60) : page.url}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className={`${styles.typeBadge} ${styles[page.page_type] || styles.general}`}>
                            {page.page_type}
                          </span>
                        </td>
                        <td>
                          <ProcessingStatusBadge status={page.processing_status} />
                        </td>
                        <td>{page.chunk_count}</td>
                        <td>{page.chunk_count - page.useless_chunks}</td>
                        <td>{formatDate(page.crawled_at)}</td>
                        <td>
                          <a
                            href={page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.actionLink}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Open
                          </a>
                        </td>
                      </tr>
                    ))}
                    {htmlPages.length === 0 && (
                      <tr>
                        <td colSpan={7} className={styles.emptyRow}>
                          No HTML pages found. Run the HTML extraction pipeline to crawl website content.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabPanel>

        {/* Sync Jobs Tab */}
        <TabPanel value="syncjobs" onActivate={fetchSyncJobs}>
          {/* Sync Health Summary - clickable cards to filter documents */}
          {syncHealth && (
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Sync Status</span>
                <StatusBadge
                  variant={
                    syncHealth.status === 'healthy' ? 'success' :
                    syncHealth.status === 'running' ? 'info' :
                    syncHealth.status === 'degraded' ? 'warning' : 'error'
                  }
                  pulse={syncHealth.status === 'running'}
                >
                  {syncHealth.status}
                </StatusBadge>
              </div>
              <button
                className={`${styles.statCard} ${styles.clickableStatCard}`}
                onClick={() => {
                  setStatusFilter('');
                  setIncludeInactive(false);
                  setActiveTab('documents');
                }}
                title="View active documents"
              >
                <span className={styles.statLabel}>Active Documents</span>
                <span className={styles.statValue}>{syncHealth.documents.total}</span>
              </button>
              <button
                className={`${styles.statCard} ${styles.clickableStatCard}`}
                onClick={() => {
                  setStatusFilter('removed');
                  setIncludeInactive(true);
                  setActiveTab('documents');
                }}
                title="View inactive/removed documents"
              >
                <span className={styles.statLabel}>Inactive/Removed</span>
                <span className={styles.statValue} style={{ color: 'var(--cm-gray-500)' }}>{syncHealth.documents.inactive || 0}</span>
              </button>
              <button
                className={`${styles.statCard} ${styles.clickableStatCard}`}
                onClick={() => {
                  setStatusFilter('completed');
                  setIncludeInactive(false);
                  setActiveTab('documents');
                }}
                title="View completed documents"
              >
                <span className={styles.statLabel}>Completed</span>
                <span className={styles.statValue}>{syncHealth.documents.by_status.completed || 0}</span>
              </button>
              <button
                className={`${styles.statCard} ${styles.clickableStatCard}`}
                onClick={() => {
                  setStatusFilter('failed');
                  setIncludeInactive(false);
                  setActiveTab('documents');
                }}
                title="View failed documents"
              >
                <span className={styles.statLabel}>Failed</span>
                <span className={styles.statValue}>{syncHealth.documents.by_status.failed || 0}</span>
              </button>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Last Sync</span>
                <span className={styles.statValue} style={{ fontSize: '0.85rem' }}>
                  {syncHealth.last_sync.started_at ? formatDate(syncHealth.last_sync.started_at) : 'Never'}
                </span>
              </div>
            </div>
          )}

          {/* Trigger Sync Card */}
          <Card className={styles.syncTriggerCard}>
            <CardHeader>
              <h3>Trigger Document Sync</h3>
            </CardHeader>
            <CardBody>
              <p className={styles.testingNote}>
                Start a new FTP document sync to discover and process new documents.
              </p>
              <div className={styles.syncTriggerForm}>
                <div className={styles.configField}>
                  <label>Batch Limit</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={syncBatchLimit}
                    onChange={(e) => setSyncBatchLimit(parseInt(e.target.value) || 10)}
                    style={{ width: '100px' }}
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={handleTriggerSync}
                  disabled={isTriggeringSync}
                  leftIcon={<PlayIcon />}
                >
                  {isTriggeringSync ? 'Starting...' : 'Start Sync'}
                </Button>
              </div>
              {syncTriggerResult && (
                <div className={syncTriggerResult.type === 'success' ? styles.actionSuccess : styles.actionError}>
                  {syncTriggerResult.message}
                  <button
                    className={styles.dismissButton}
                    onClick={() => setSyncTriggerResult(null)}
                  >
                    &times;
                  </button>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Sync Jobs Table */}
          <Card padding="none" className={styles.tableCard}>
            <CardHeader
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchSyncJobs}
                  leftIcon={<RefreshIcon />}
                >
                  Refresh
                </Button>
              }
            >
              <h3>Sync Job History</h3>
            </CardHeader>
            {isLoadingSyncJobs ? (
              <div className={styles.loading}>
                <div className={styles.spinner} />
                <p>Loading sync jobs...</p>
              </div>
            ) : (
              <>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Started</th>
                        <th>Status</th>
                        <th>Duration</th>
                        <th>Scanned</th>
                        <th>New</th>
                        <th>Updated</th>
                        <th>Skipped</th>
                        <th>Failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncJobs.length > 0 ? (
                        syncJobs.map((job) => (
                          <tr key={job.id}>
                            <td>{formatDate(job.started_at)}</td>
                            <td>
                              <StatusBadge
                                variant={
                                  job.status === 'completed' ? 'success' :
                                  job.status === 'running' ? 'info' :
                                  job.status === 'failed' ? 'error' : 'warning'
                                }
                                pulse={job.status === 'running'}
                              >
                                {job.status}
                              </StatusBadge>
                            </td>
                            <td>{formatDuration(job.started_at, job.completed_at)}</td>
                            <td>{job.documents_scanned}</td>
                            <td>{job.documents_new}</td>
                            <td>{job.documents_updated}</td>
                            <td>{job.documents_skipped}</td>
                            <td className={job.documents_failed > 0 ? styles.failedCount : ''}>
                              {job.documents_failed}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className={styles.emptyRow}>
                            No sync jobs found. Trigger a sync to get started.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className={styles.pagination}>
                  <span className={styles.paginationInfo}>
                    Showing {syncJobsOffset + 1}-{Math.min(syncJobsOffset + syncJobsLimit, syncJobsTotal)} of {syncJobsTotal} jobs
                  </span>
                  <div className={styles.paginationButtons}>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={syncJobsOffset === 0}
                      onClick={() => setSyncJobsOffset(Math.max(0, syncJobsOffset - syncJobsLimit))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={syncJobsOffset + syncJobsLimit >= syncJobsTotal}
                      onClick={() => setSyncJobsOffset(syncJobsOffset + syncJobsLimit)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </TabPanel>

        {/* Trip Report Chunks Tab */}
        <TabPanel value="tripreports" onActivate={fetchTripChunks}>
          {/* Stats Summary */}
          {tripChunksStats && (
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Total Chunks</span>
                <span className={styles.statValue}>{tripChunksStats.total.toLocaleString()}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>With Raw Text</span>
                <span className={styles.statValue}>{tripChunksStats.with_raw_text.toLocaleString()}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Missing Raw Text</span>
                <span className={styles.statValue} style={{ color: tripChunksStats.missing_raw_text > 0 ? 'var(--cm-orange-500)' : undefined }}>
                  {tripChunksStats.missing_raw_text.toLocaleString()}
                </span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Coverage</span>
                <span className={styles.statValue}>{tripChunksStats.raw_text_coverage_pct}%</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>Uploaded to Vertex</span>
                <span className={styles.statValue}>{tripChunksStats.uploaded_to_vertex.toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* Type Breakdown */}
          {tripChunksStats && Object.keys(tripChunksStats.by_type).length > 0 && (
            <Card className={styles.tableCard}>
              <CardHeader>
                <h3>Raw Text Coverage by Chunk Type</h3>
              </CardHeader>
              <CardBody>
                <div className={styles.statusGrid}>
                  {Object.entries(tripChunksStats.by_type)
                    .sort((a, b) => b[1].total - a[1].total)
                    .map(([type, data]) => (
                      <div key={type} className={styles.statusItem}>
                        <span className={`${styles.typeBadge} ${styles[type.toLowerCase()] || styles.unknown}`}>{type}</span>
                        <span className={styles.statusCount}>
                          {data.with_raw_text}/{data.total}
                          <span style={{ fontSize: '0.8em', color: 'var(--cm-gray-500)', marginLeft: '4px' }}>
                            ({data.total > 0 ? Math.round(100 * data.with_raw_text / data.total) : 0}%)
                          </span>
                        </span>
                      </div>
                    ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Filters */}
          <div className={styles.tabHeader}>
            <div className={styles.filters}>
              <select
                className={styles.filterSelect}
                value={tripChunksTypeFilter}
                onChange={(e) => setTripChunksTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="COMPANY_PROFILE">Company Profile</option>
                <option value="VISIT">Visit</option>
                <option value="CALL">Call</option>
                <option value="EMAIL">Email</option>
                <option value="MEETING">Meeting</option>
                <option value="ORDER">Order</option>
                <option value="QUOTE">Quote</option>
                <option value="FOLLOW_UP">Follow Up</option>
                <option value="ISSUE">Issue</option>
                <option value="OTHER">Other</option>
              </select>
              <select
                className={styles.filterSelect}
                value={tripChunksRawTextFilter}
                onChange={(e) => setTripChunksRawTextFilter(e.target.value)}
              >
                <option value="">All Raw Text</option>
                <option value="true">Has Raw Text</option>
                <option value="false">Missing Raw Text</option>
              </select>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search chunk text or raw text..."
                value={tripChunksSearch}
                onChange={(e) => setTripChunksSearch(e.target.value)}
                style={{ maxWidth: '250px' }}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchTripChunks}
              leftIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </div>

          {/* Trip Report Chunks List */}
          <Card padding="none" className={styles.tableCard}>
            {isLoadingTripChunks ? (
              <div className={styles.loading}>
                <div className={styles.spinner} />
                <p>Loading trip report chunks...</p>
              </div>
            ) : (
              <>
                <div className={styles.chunksList}>
                  {tripChunks.length > 0 ? (
                    tripChunks.map((chunk) => {
                      const isExpanded = expandedTripChunkIds.has(chunk.id);
                      return (
                        <div
                          key={chunk.id}
                          className={`${styles.chunkListItem} ${isExpanded ? styles.expanded : ''}`}
                        >
                          <div className={styles.chunkListHeader} onClick={() => toggleTripChunkExpand(chunk.id)}>
                            <div className={styles.chunkListLeft}>
                              <span className={styles.chunkListIndex}>#{chunk.chunk_index}</span>
                              <span className={styles.chunkListFilename}>{chunk.company_name}</span>
                              <span className={`${styles.typeBadge} ${styles[chunk.chunk_type.toLowerCase()] || styles.unknown}`}>
                                {chunk.chunk_type}
                              </span>
                              {chunk.event_date && <span className={styles.chunkListMeta}>{chunk.event_date}</span>}
                            </div>
                            <div className={styles.chunkListRight}>
                              {chunk.is_useless && <StatusBadge variant="warning">Skip</StatusBadge>}
                              {chunk.raw_text ? (
                                <StatusBadge variant="success">Raw Text</StatusBadge>
                              ) : (
                                <StatusBadge variant="error">No Raw Text</StatusBadge>
                              )}
                              {chunk.vertex_doc_id && <StatusBadge variant="info">Indexed</StatusBadge>}
                              <span className={styles.expandIndicator}>{isExpanded ? '−' : '+'}</span>
                            </div>
                          </div>

                          {/* Preview line */}
                          {!isExpanded && (
                            <div className={styles.chunkListPreview}>
                              {chunk.raw_text || chunk.summary || chunk.chunk_text}
                            </div>
                          )}

                          {/* Expanded content */}
                          {isExpanded && (
                            <div className={styles.chunkListExpanded}>
                              {chunk.raw_text && (
                                <div className={styles.chunkListText}>
                                  <strong>Raw Text ({chunk.raw_text_length} chars):</strong>
                                  <pre>{chunk.raw_text}</pre>
                                </div>
                              )}
                              {chunk.summary && (
                                <div className={styles.chunkListSummary}>
                                  <strong>Summary ({chunk.summary_length} chars):</strong> {chunk.summary}
                                </div>
                              )}
                              {chunk.chunk_text && chunk.chunk_text !== chunk.summary && chunk.chunk_text !== chunk.raw_text && (
                                <div className={styles.chunkListText}>
                                  <strong>Chunk Text ({chunk.chunk_text_length} chars):</strong>
                                  <pre>{chunk.chunk_text}</pre>
                                </div>
                              )}
                              <div className={styles.chunkListFooter}>
                                <span>Company: {chunk.company_name}</span>
                                <span>Salesperson: {chunk.salesperson_id}</span>
                                {chunk.sentiment && <span>Sentiment: {chunk.sentiment}</span>}
                                {chunk.source_file && <span>Source: {chunk.source_file.length > 30 ? '...' + chunk.source_file.slice(-30) : chunk.source_file}</span>}
                              </div>
                              {(chunk.contacts_mentioned?.length || chunk.products_mentioned?.length || chunk.action_items?.length) ? (
                                <div className={styles.chunkListFooter}>
                                  {chunk.contacts_mentioned && chunk.contacts_mentioned.length > 0 && (
                                    <span>Contacts: {chunk.contacts_mentioned.join(', ')}</span>
                                  )}
                                  {chunk.products_mentioned && chunk.products_mentioned.length > 0 && (
                                    <span>Products: {chunk.products_mentioned.join(', ')}</span>
                                  )}
                                  {chunk.action_items && chunk.action_items.length > 0 && (
                                    <span>Actions: {chunk.action_items.join('; ')}</span>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.emptySearch}>
                      No trip report chunks found matching filters
                    </div>
                  )}
                </div>

                {/* Pagination */}
                <div className={styles.pagination}>
                  <span className={styles.paginationInfo}>
                    Showing {tripChunksOffset + 1}-{Math.min(tripChunksOffset + tripChunksLimit, tripChunksTotal)} of {tripChunksTotal} chunks
                  </span>
                  <div className={styles.paginationButtons}>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={tripChunksOffset === 0}
                      onClick={() => setTripChunksOffset(Math.max(0, tripChunksOffset - tripChunksLimit))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={tripChunksOffset + tripChunksLimit >= tripChunksTotal}
                      onClick={() => setTripChunksOffset(tripChunksOffset + tripChunksLimit)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </TabPanel>

        {/* Search Tab */}
        <TabPanel value="search">
          <Card>
            <CardHeader
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                >
                  {showAdvancedSearch ? 'Hide Options' : 'Show Options'}
                </Button>
              }
            >
              <h3>Vertex AI Search</h3>
            </CardHeader>
            <CardBody>
              {/* Query Input */}
              <div className={styles.searchBox}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search documents and chunks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSearch}
                  disabled={isSearching || !searchQuery.trim()}
                  leftIcon={<SearchIcon />}
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </Button>
              </div>

              {/* Advanced Search Options */}
              {showAdvancedSearch && (
                <div className={styles.advancedSearch}>
                  <h4>Search Configuration</h4>

                  {/* Content Source Selector - Hierarchical */}
                  <div className={styles.configSection}>
                    <label>Content Source</label>
                    <p className={styles.filterHint}>Select a content source to search, then optionally filter by sub-type</p>

                    <div className={styles.sourceOptions}>
                      {/* All Sources */}
                      <label className={`${styles.sourceOption} ${searchConfig.filters.document_types.length === 0 && searchConfig.filters.categories.length === 0 ? styles.sourceSelected : ''}`}>
                        <input
                          type="radio"
                          name="contentSource"
                          checked={searchConfig.filters.document_types.length === 0 && searchConfig.filters.categories.length === 0}
                          onChange={() => {
                            setSearchConfig({
                              ...searchConfig,
                              filters: {
                                ...searchConfig.filters,
                                document_types: [],
                                doc_types: [],
                                page_types: [],
                                categories: [],
                              },
                            });
                          }}
                        />
                        <span className={styles.sourceRadio} />
                        <span className={styles.sourceLabel}>All Sources</span>
                        <span className={styles.sourceDesc}>Search everything</span>
                      </label>

                      {/* PDF Documents */}
                      <label className={`${styles.sourceOption} ${styles.sourcePdf} ${searchConfig.filters.document_types.includes('pdf_document') ? styles.sourceSelected : ''}`}>
                        <input
                          type="radio"
                          name="contentSource"
                          checked={searchConfig.filters.document_types.includes('pdf_document')}
                          onChange={() => {
                            setSearchConfig({
                              ...searchConfig,
                              filters: {
                                ...searchConfig.filters,
                                document_types: ['pdf_document'],
                                page_types: [],
                                categories: [],
                              },
                            });
                          }}
                        />
                        <span className={styles.sourceRadio} />
                        <span className={styles.sourceLabel}>PDF Documents</span>
                        <span className={styles.sourceDesc}>SDS, TDS, LEED, COA from FTP</span>
                      </label>

                      {/* Website Content */}
                      <label className={`${styles.sourceOption} ${styles.sourceWebsite} ${searchConfig.filters.document_types.includes('website_content') ? styles.sourceSelected : ''}`}>
                        <input
                          type="radio"
                          name="contentSource"
                          checked={searchConfig.filters.document_types.includes('website_content')}
                          onChange={() => {
                            setSearchConfig({
                              ...searchConfig,
                              filters: {
                                ...searchConfig.filters,
                                document_types: ['website_content'],
                                doc_types: [],
                                categories: [],
                              },
                            });
                          }}
                        />
                        <span className={styles.sourceRadio} />
                        <span className={styles.sourceLabel}>Website Content</span>
                        <span className={styles.sourceDesc}>chemmasters.net pages</span>
                      </label>

                      {/* Trip Reports */}
                      <label className={`${styles.sourceOption} ${styles.sourceTrips} ${searchConfig.filters.categories.includes('sales') ? styles.sourceSelected : ''}`}>
                        <input
                          type="radio"
                          name="contentSource"
                          checked={searchConfig.filters.categories.includes('sales')}
                          onChange={() => {
                            setSearchConfig({
                              ...searchConfig,
                              filters: {
                                ...searchConfig.filters,
                                document_types: [],
                                doc_types: [],
                                page_types: [],
                                categories: ['sales'],
                              },
                            });
                          }}
                        />
                        <span className={styles.sourceRadio} />
                        <span className={styles.sourceLabel}>Trip Reports</span>
                        <span className={styles.sourceDesc}>Sales copilot data</span>
                      </label>
                    </div>

                    {/* PDF Document Sub-filters */}
                    {searchConfig.filters.document_types.includes('pdf_document') && (
                      <div className={styles.subFilters}>
                        <div className={styles.subFiltersTitle}>Document Type</div>
                        <div className={styles.checkboxGroup}>
                          {[
                            { value: 'sds', label: 'SDS (Safety)' },
                            { value: 'tds', label: 'TDS (Technical)' },
                            { value: 'leed', label: 'LEED (Certification)' },
                            { value: 'coa', label: 'COA (Analysis)' },
                            { value: 'spec', label: 'SPEC (Specifications)' },
                          ].map(({ value, label }) => (
                            <label key={value} className={`${styles.checkboxItem} ${searchConfig.filters.doc_types.includes(value) ? styles.checkboxItemChecked : ''}`}>
                              <input
                                type="checkbox"
                                checked={searchConfig.filters.doc_types.includes(value)}
                                onChange={(e) => {
                                  const newTypes = e.target.checked
                                    ? [...searchConfig.filters.doc_types, value]
                                    : searchConfig.filters.doc_types.filter((t) => t !== value);
                                  setSearchConfig({
                                    ...searchConfig,
                                    filters: { ...searchConfig.filters, doc_types: newTypes },
                                  });
                                }}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Website Content Sub-filters */}
                    {searchConfig.filters.document_types.includes('website_content') && (
                      <div className={styles.subFilters}>
                        <div className={styles.subFiltersTitle}>Page Type</div>
                        <div className={styles.checkboxGroup}>
                          {[
                            { value: 'product', label: 'Product Pages' },
                            { value: 'resource', label: 'Resource Pages' },
                            { value: 'support', label: 'Support Pages' },
                            { value: 'market', label: 'Market Pages' },
                            { value: 'about', label: 'About Pages' },
                            { value: 'general', label: 'General Pages' },
                          ].map(({ value, label }) => (
                            <label key={value} className={`${styles.checkboxItem} ${searchConfig.filters.page_types.includes(value) ? styles.checkboxItemChecked : ''}`}>
                              <input
                                type="checkbox"
                                checked={searchConfig.filters.page_types.includes(value)}
                                onChange={(e) => {
                                  const newTypes = e.target.checked
                                    ? [...searchConfig.filters.page_types, value]
                                    : searchConfig.filters.page_types.filter((t) => t !== value);
                                  setSearchConfig({
                                    ...searchConfig,
                                    filters: { ...searchConfig.filters, page_types: newTypes },
                                  });
                                }}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Trip Reports Sub-filters */}
                    {searchConfig.filters.categories.includes('sales') && (
                      <div className={styles.subFilters}>
                        <div className={styles.subFiltersTitle}>Chunk Type</div>
                        <div className={styles.checkboxGroup}>
                          {[
                            { value: 'company_profile', label: 'Company Profiles' },
                            { value: 'sales_event', label: 'Sales Events' },
                          ].map(({ value, label }) => (
                            <label key={value} className={`${styles.checkboxItem} ${searchConfig.filters.doc_types.includes(value) ? styles.checkboxItemChecked : ''}`}>
                              <input
                                type="checkbox"
                                checked={searchConfig.filters.doc_types.includes(value)}
                                onChange={(e) => {
                                  const newTypes = e.target.checked
                                    ? [...searchConfig.filters.doc_types, value]
                                    : searchConfig.filters.doc_types.filter((t) => t !== value);
                                  setSearchConfig({
                                    ...searchConfig,
                                    filters: { ...searchConfig.filters, doc_types: newTypes },
                                  });
                                }}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Result Limits */}
                  <div className={styles.configRow}>
                    <div className={styles.configField}>
                      <label>Page Size</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={searchConfig.page_size}
                        onChange={(e) =>
                          setSearchConfig({ ...searchConfig, page_size: parseInt(e.target.value) || 10 })
                        }
                      />
                    </div>
                    <div className={styles.configField}>
                      <label>Top K (Initial Retrieval)</label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={searchConfig.top_k}
                        onChange={(e) =>
                          setSearchConfig({ ...searchConfig, top_k: parseInt(e.target.value) || 20 })
                        }
                      />
                    </div>
                  </div>

                  {/* Query Enhancements */}
                  <div className={styles.configRow}>
                    <div className={styles.configField}>
                      <label>Query Expansion</label>
                      <select
                        value={searchConfig.query_expansion}
                        onChange={(e) =>
                          setSearchConfig({ ...searchConfig, query_expansion: e.target.value as QueryExpansion })
                        }
                      >
                        <option value="AUTO">Auto</option>
                        <option value="DISABLED">Disabled</option>
                      </select>
                    </div>
                    <div className={styles.configField}>
                      <label>Spell Correction</label>
                      <select
                        value={searchConfig.spell_correction}
                        onChange={(e) =>
                          setSearchConfig({ ...searchConfig, spell_correction: e.target.value as SpellCorrection })
                        }
                      >
                        <option value="AUTO">Auto</option>
                        <option value="OFF">Off</option>
                      </select>
                    </div>
                  </div>

                  {/* Reranking */}
                  <div className={styles.configSection}>
                    <label className={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={searchConfig.enable_reranking}
                        onChange={(e) =>
                          setSearchConfig({ ...searchConfig, enable_reranking: e.target.checked })
                        }
                      />
                      Enable Semantic Reranking
                    </label>

                    {searchConfig.enable_reranking && (
                      <div className={styles.configRow}>
                        <div className={styles.configField}>
                          <label>Reranker Model</label>
                          <select
                            value={searchConfig.reranker_model}
                            onChange={(e) =>
                              setSearchConfig({ ...searchConfig, reranker_model: e.target.value as RerankerModel })
                            }
                          >
                            <option value="semantic-ranker-default-004">Default v004 (Most Accurate)</option>
                            <option value="semantic-ranker-fast-004">Fast v004</option>
                            <option value="semantic-ranker-default-003">Default v003 (Legacy)</option>
                          </select>
                        </div>
                        <div className={styles.configField}>
                          <label>Rerank Top N</label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={searchConfig.rerank_top_n}
                            onChange={(e) =>
                              setSearchConfig({ ...searchConfig, rerank_top_n: parseInt(e.target.value) || 10 })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content Extraction */}
                  <div className={styles.configRow}>
                    <div className={styles.configField}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={searchConfig.include_snippets}
                          onChange={(e) =>
                            setSearchConfig({ ...searchConfig, include_snippets: e.target.checked })
                          }
                        />
                        Include Snippets
                      </label>
                    </div>
                    <div className={styles.configField}>
                      <label>Max Extractive Answers</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={searchConfig.max_extractive_answers}
                        onChange={(e) =>
                          setSearchConfig({ ...searchConfig, max_extractive_answers: parseInt(e.target.value) || 3 })
                        }
                      />
                    </div>
                  </div>

                  {/* Save Config */}
                  <div className={styles.saveConfigSection}>
                    <h4>Save Configuration for A/B Testing</h4>
                    <div className={styles.configRow}>
                      <input
                        type="text"
                        placeholder="Configuration name..."
                        value={saveConfigName}
                        onChange={(e) => setSaveConfigName(e.target.value)}
                        className={styles.configNameInput}
                      />
                      <input
                        type="text"
                        placeholder="Description (optional)..."
                        value={saveConfigDescription}
                        onChange={(e) => setSaveConfigDescription(e.target.value)}
                        className={styles.configDescInput}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSaveConfig}
                        disabled={!saveConfigName.trim()}
                      >
                        Save Config
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Results */}
              {searchResults !== null && (
                <div className={styles.searchResults}>
                  <div className={styles.searchMeta}>
                    <span>Found {searchResults.total_results} results in {searchResults.latency_ms}ms</span>
                    {searchResults.reranking_applied && <StatusBadge variant="info">Reranked</StatusBadge>}
                    {searchResults.filter_applied && (
                      <span className={styles.filterApplied}>Filter: {searchResults.filter_applied}</span>
                    )}
                  </div>

                  {searchResults.results.length === 0 ? (
                    <p className={styles.emptySearch}>
                      No results found. Try broadening your search or adjusting filters.
                    </p>
                  ) : (
                    <div className={styles.chunksList}>
                      {searchResults.results.map((result, idx) => {
                        const isExpanded = expandedSearchResultIds.has(result.id);
                        // Extract data from raw_data (Vertex AI Search struct_data)
                        const rawData = result.raw_data || {};
                        const summary = result.content || (typeof rawData.summary === 'string' ? rawData.summary : '');
                        const rawText = typeof rawData.chunk_text === 'string'
                          ? rawData.chunk_text
                          : (typeof rawData.raw_content === 'string' ? rawData.raw_content : '');
                        const tokenCount = typeof rawData.token_count === 'number' ? rawData.token_count : null;

                        return (
                          <div
                            key={result.id}
                            className={`${styles.chunkListItem} ${isExpanded ? styles.expanded : ''}`}
                          >
                            <div className={styles.chunkListHeader} onClick={() => toggleSearchResultExpand(result.id)}>
                              <div className={styles.chunkListLeft}>
                                <span className={styles.chunkListIndex}>#{idx + 1}</span>
                                <span className={styles.chunkListFilename}>{result.source_document}</span>
                                <TypeBadge type={result.doc_type} />
                                {tokenCount && <span className={styles.chunkListMeta}>{tokenCount} tok</span>}
                                {result.page_number && <span className={styles.chunkListMeta}>p{result.page_number}</span>}
                              </div>
                              <div className={styles.chunkListRight}>
                                {result.relevance_score && (
                                  <span className={styles.resultScore}>
                                    Score: {result.relevance_score.toFixed(3)}
                                  </span>
                                )}
                                {summary && <StatusBadge variant="success">Summary</StatusBadge>}
                                <span className={styles.expandIndicator}>{isExpanded ? '−' : '+'}</span>
                              </div>
                            </div>

                            {/* Preview line when collapsed */}
                            {!isExpanded && (
                              <div className={styles.chunkListPreview}>
                                {summary || rawText || '(No content available)'}
                              </div>
                            )}

                            {/* Expanded content */}
                            {isExpanded && (
                              <div className={styles.chunkListExpanded}>
                                {result.heading_context && (
                                  <div className={styles.chunkListHeading}>
                                    <strong>Section:</strong> {result.heading_context}
                                  </div>
                                )}
                                {summary && (
                                  <div className={styles.chunkListSummary}>
                                    <strong>Summary:</strong> {summary}
                                  </div>
                                )}
                                {rawText && (
                                  <div className={styles.chunkListText}>
                                    <strong>Raw Text:</strong>
                                    <pre>{rawText}</pre>
                                  </div>
                                )}
                                <div className={styles.chunkListFooter}>
                                  {tokenCount && <span>Tokens: {tokenCount}</span>}
                                  {result.chunk_index !== null && <span>Chunk #{result.chunk_index}</span>}
                                  <span>Category: {result.category || 'N/A'}</span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isLoadingLlmPayload}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      // Call backend API to get properly formatted LLM payload
                                      // This uses ChunkLLMPayload.from_search_result_item() on the backend
                                      setIsLoadingLlmPayload(true);
                                      try {
                                        const searchResultItem: SearchResultItem = {
                                          id: result.id,
                                          content: result.content || '',
                                          heading_context: result.heading_context || '',
                                          doc_type: result.doc_type || '',
                                          category: result.category || '',
                                          source_document: result.source_document || '',
                                          page_number: result.page_number,
                                          chunk_index: result.chunk_index,
                                          relevance_score: result.relevance_score,
                                          snippet: result.snippet || null,
                                          raw_data: result.raw_data || {},
                                        };
                                        const payload = await adminService.getSearchResultLLMPayload(searchResultItem);
                                        setLlmPayload(payload);
                                      } catch (error) {
                                        console.error('Failed to load LLM payload:', error);
                                        alert('Failed to load LLM payload. See console for details.');
                                      } finally {
                                        setIsLoadingLlmPayload(false);
                                      }
                                    }}
                                  >
                                    {isLoadingLlmPayload ? 'Loading...' : 'View LLM Payload'}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </TabPanel>

        {/* Testing Tab - A/B Testing */}
        <TabPanel value="testing" onActivate={fetchTestingData}>
          <div className={styles.testingGrid}>
            {/* Saved Configurations */}
            <Card>
              <CardHeader
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchTestingData}
                    leftIcon={<RefreshIcon />}
                  >
                    Refresh
                  </Button>
                }
              >
                <h3>Saved Configurations</h3>
              </CardHeader>
              <CardBody>
                {isLoadingConfigs ? (
                  <div className={styles.loading}>
                    <div className={styles.spinner} />
                  </div>
                ) : savedConfigs.length === 0 ? (
                  <p className={styles.emptyState}>
                    No saved configurations. Create one from the Search tab.
                  </p>
                ) : (
                  <div className={styles.configsList}>
                    {savedConfigs.map((config) => (
                      <div key={config.id} className={styles.configItem}>
                        <div className={styles.configInfo}>
                          <strong>{config.name}</strong>
                          {config.description && <p>{config.description}</p>}
                          <span className={styles.configDate}>
                            Created: {formatDate(config.created_at)}
                          </span>
                        </div>
                        <div className={styles.configActions}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedConfigForTest(config.id)}
                          >
                            Select
                          </Button>
                          <button
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            onClick={() => handleDeleteConfig(config.id)}
                            title="Deactivate"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Test Suite Runner */}
            <Card>
              <CardHeader>
                <h3>Run Test Suite</h3>
              </CardHeader>
              <CardBody>
                <p className={styles.testingNote}>
                  Run all test cases against a saved configuration to evaluate search quality.
                </p>

                <div className={styles.testSuiteForm}>
                  <div className={styles.configField}>
                    <label>Select Configuration</label>
                    <select
                      value={selectedConfigForTest}
                      onChange={(e) => setSelectedConfigForTest(e.target.value)}
                      className={styles.configSelect}
                    >
                      <option value="">-- Select a configuration --</option>
                      {savedConfigs.map((config) => (
                        <option key={config.id} value={config.id}>
                          {config.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Button
                    variant="primary"
                    onClick={handleRunTestSuite}
                    disabled={!selectedConfigForTest || isRunningTestSuite}
                    leftIcon={<PlayIcon />}
                  >
                    {isRunningTestSuite ? 'Running...' : 'Run Test Suite'}
                  </Button>
                </div>

                {/* Test Cases Summary */}
                <div className={styles.testCasesSummary}>
                  <h4>Test Cases ({testCases.length})</h4>
                  {isLoadingTestCases ? (
                    <div className={styles.loading}>
                      <div className={styles.spinner} />
                    </div>
                  ) : testCases.length === 0 ? (
                    <p className={styles.emptyState}>No test cases defined.</p>
                  ) : (
                    <div className={styles.testCasesList}>
                      {testCases.map((tc) => (
                        <div key={tc.id} className={styles.testCaseItem}>
                          <span className={styles.testCaseName}>{tc.name}</span>
                          <span className={styles.testCaseCategory}>{tc.category}</span>
                          <span className={styles.testCaseQuery}>{tc.query}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Test Results */}
            <Card className={styles.fullWidth}>
              <CardHeader>
                <h3>Recent Test Runs</h3>
              </CardHeader>
              <CardBody>
                {testRuns.length === 0 ? (
                  <p className={styles.emptyState}>No test runs yet.</p>
                ) : (
                  <div className={styles.testRunsList}>
                    {testRuns.map((run) => (
                      <div key={run.id} className={styles.testRunItem}>
                        <div className={styles.testRunHeader}>
                          <span className={styles.testRunDate}>{formatDate(run.started_at)}</span>
                          <StatusBadge
                            variant={run.completed_at ? (run.passed_cases === run.total_cases ? 'success' : 'warning') : 'info'}
                          >
                            {run.completed_at ? `${run.passed_cases}/${run.total_cases} passed` : 'Running'}
                          </StatusBadge>
                        </div>
                        {run.results_summary && (
                          <div className={styles.testRunStats}>
                            <span>Pass Rate: {(run.results_summary.pass_rate * 100).toFixed(1)}%</span>
                            <span>Avg Latency: {run.results_summary.avg_latency_ms}ms</span>
                            <span>Keyword Hit Rate: {(run.results_summary.keyword_hit_rate * 100).toFixed(1)}%</span>
                            <span>Doc Type Match: {(run.results_summary.doc_type_match_rate * 100).toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Document Processing - Moved to bottom */}
          <div style={{ marginTop: '1rem' }}>
          <Card>
            <CardHeader>
              <h3>Document Processing</h3>
            </CardHeader>
            <CardBody>
              <p className={styles.testingNote}>
                Process documents from FTP to populate the search index.
              </p>

              <div className={styles.testingActions}>
                <Button
                  variant="primary"
                  onClick={() => handleProcessNext(false)}
                  disabled={isProcessing}
                  leftIcon={<PlayIcon />}
                >
                  {isProcessing ? 'Processing...' : 'Process Next Document'}
                </Button>
              </div>

              {/* Processing Result */}
              {processResult && (
                <div className={
                  processResult.status === 'failed' || processResult.status === 'timeout'
                    ? styles.resultError
                    : processResult.status === 'processing' || processResult.status === 'queued'
                    ? styles.resultProcessing
                    : styles.resultSuccess
                }>
                  {processResult.status === 'completed' ? (
                    <div>
                      <p><strong>Processed:</strong> {processResult.filename}</p>
                      <p><strong>Chunks created:</strong> {processResult.chunk_count}</p>
                    </div>
                  ) : processResult.status === 'processing' || processResult.status === 'queued' ? (
                    <div className={styles.processingStatus}>
                      <div className={styles.spinner} style={{ width: '20px', height: '20px', marginRight: '12px' }} />
                      <p>{processResult.message || `Processing ${processResult.filename}...`}</p>
                    </div>
                  ) : processResult.status === 'no_candidates' ? (
                    <p>No unprocessed documents found on FTP.</p>
                  ) : processResult.status === 'timeout' ? (
                    <p>{processResult.message}</p>
                  ) : (
                    <p>{processResult.error || 'Processing failed'}</p>
                  )}
                </div>
              )}

              <div className={styles.statusSummary}>
                <h4>Document Status Summary</h4>
                <div className={styles.statusGrid}>
                  {stats && Object.entries(stats.documents.by_status).map(([status, count]) => (
                    <div key={status} className={styles.statusItem}>
                      <ProcessingStatusBadge status={status} />
                      <span className={styles.statusCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
          </div>
        </TabPanel>

        {/* Ingestion Pipeline Tab */}
        <TabPanel value="ingestion">
          <IngestionAdminTab />
        </TabPanel>
      </Tabs>

      {/* Document Viewer Modal with PDF and bounding boxes */}
      {selectedDoc && (
        <DocumentViewerModal
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
        />
      )}

      {isLoadingDoc && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <p>Loading document...</p>
          </div>
        </div>
      )}

      {/* LLM Payload Modal - handles both chunk and search result payloads */}
      {llmPayload && (
        <div className={styles.modalOverlay} onClick={() => setLlmPayload(null)}>
          <div className={styles.llmPayloadModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>LLM Payload {('vertex_doc_id' in llmPayload) ? '(Search Result)' : '(Database Chunk)'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setLlmPayload(null)}>
                Close
              </Button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.llmPayloadSection}>
                <h4>Source</h4>
                {('vertex_doc_id' in llmPayload) ? (
                  <>
                    <p><strong>Vertex Doc ID:</strong> {llmPayload.vertex_doc_id}</p>
                    <p><strong>Source:</strong> {llmPayload.source_document}</p>
                    <p><strong>Source Type:</strong> {llmPayload.source_type}</p>
                    <p><strong>Chunk Index:</strong> {llmPayload.metadata.chunk_index ?? 'N/A'}</p>
                    <p><strong>Page:</strong> {llmPayload.metadata.page_number ?? 'N/A'}</p>
                    <p><strong>Doc Type:</strong> {llmPayload.metadata.doc_type}</p>
                    <p><strong>Category:</strong> {llmPayload.metadata.category}</p>
                    {llmPayload.metadata.relevance_score && (
                      <p><strong>Relevance Score:</strong> {llmPayload.metadata.relevance_score.toFixed(4)}</p>
                    )}
                  </>
                ) : (
                  <>
                    <p><strong>Document:</strong> {llmPayload.document_filename}</p>
                    <p><strong>Chunk Index:</strong> {llmPayload.raw_fields.chunk_index}</p>
                    <p><strong>Page:</strong> {llmPayload.raw_fields.page_number ?? 'N/A'}</p>
                    <p><strong>Doc Type:</strong> {llmPayload.raw_fields.doc_type}</p>
                  </>
                )}
              </div>

              <div className={styles.llmPayloadSection}>
                <h4>Metadata</h4>
                <p><strong>Content Length:</strong> {llmPayload.metadata.content_length} chars</p>
                <p><strong>Raw Text Length:</strong> {llmPayload.metadata.raw_text_length} chars</p>
                {('vertex_doc_id' in llmPayload) ? (
                  <>
                    {/* Search result metadata */}
                  </>
                ) : (
                  <>
                    <p><strong>Has Summary:</strong> {llmPayload.metadata.has_summary ? 'Yes' : 'No'}</p>
                    <p><strong>Is Useless:</strong> {llmPayload.metadata.is_useless ? 'Yes' : 'No'}</p>
                    <p><strong>Token Count:</strong> {llmPayload.metadata.token_count}</p>
                    {llmPayload.metadata.summary_tokens > 0 && (
                      <p><strong>Summary Tokens:</strong> {llmPayload.metadata.summary_tokens}</p>
                    )}
                  </>
                )}
              </div>

              <div className={styles.llmPayloadSection}>
                <h4>Formatted Context (What LLM Sees)</h4>
                <pre className={styles.formattedContext}>{llmPayload.formatted_context}</pre>
              </div>

              {('mcp_format' in llmPayload) && (
                <div className={styles.llmPayloadSection}>
                  <h4>MCP Tool Format (What MCP Tools Return)</h4>
                  <pre className={styles.rawFields}>{JSON.stringify(llmPayload.mcp_format, null, 2)}</pre>
                </div>
              )}

              <div className={styles.llmPayloadSection}>
                <h4>Raw Fields (Structured Data)</h4>
                <pre className={styles.rawFields}>{JSON.stringify(llmPayload.raw_fields, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoadingLlmPayload && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <p>Loading LLM payload...</p>
          </div>
        </div>
      )}

      {/* HTML Page Viewer Modal */}
      {selectedHtmlPage && (
        <HtmlPageViewerModal
          page={selectedHtmlPage}
          onClose={() => setSelectedHtmlPage(null)}
        />
      )}

      {isLoadingHtmlPage && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <p>Loading HTML page...</p>
          </div>
        </div>
      )}
    </div>
  );
}
